import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Storefront, Play, CircleNotch, CheckCircle, XCircle, WarningCircle,
  Receipt, ArrowClockwise, Database, Key, Copy, Eye, EyeSlash,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, Loading } from "@/components/riq";
import { ScraperRetailers } from "@/components/ScraperRetailers";

const STATUS_META = {
  running: { label: "Running", cls: "bg-riq-gold/20 text-[#B25E00]", icon: CircleNotch, spin: true },
  success: { label: "Success", cls: "bg-[#E4F7ED] text-[#1f7a48]", icon: CheckCircle },
  partial: { label: "Partial", cls: "bg-riq-gold/20 text-[#B25E00]", icon: WarningCircle },
  error: { label: "Error", cls: "bg-red-100 text-red-600", icon: XCircle },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.error;
  const Icon = m.icon;
  return (
    <span data-testid={`run-status-${status}`} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${m.cls}`}>
      <Icon size={13} weight="bold" className={m.spin ? "animate-spin" : ""} /> {m.label}
    </span>
  );
}

export default function AdminScraper() {
  const [providers, setProviders] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [queries, setQueries] = useState([]);
  const [runs, setRuns] = useState([]);
  const [retailer, setRetailer] = useState("takealot");
  const [useReceipts, setUseReceipts] = useState(true);
  const [customText, setCustomText] = useState("");
  const [activeRun, setActiveRun] = useState(null);
  const [starting, setStarting] = useState(false);
  const [ingest, setIngest] = useState(null);
  const [showKey, setShowKey] = useState(false);
  const pollRef = useRef(null);

  const loadProviders = () => api.get("/admin/scraper/providers").then((r) => { setProviders(r.data.providers); setSchedule(r.data.schedule); }).catch(() => setProviders([]));
  const loadRuns = () => api.get("/admin/scraper/runs?limit=15").then((r) => setRuns(r.data.runs)).catch(() => {});
  const loadIngest = () => api.get("/admin/scraper/ingest-key").then((r) => setIngest(r.data)).catch(() => {});

  useEffect(() => {
    loadProviders();
    loadRuns();
    loadIngest();
    api.get("/admin/scraper/queries?limit=30").then((r) => setQueries(r.data.queries)).catch(() => {});
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Copied to clipboard"); };

  const rotateKey = async () => {
    if (!window.confirm("Generate a new ingest key? Your worker must be updated with the new key, and the old one stops working immediately.")) return;
    try {
      await api.post("/admin/scraper/ingest-key/rotate");
      await loadIngest();
      setShowKey(true);
      toast.success("New ingest key generated — update your worker");
    } catch { toast.error("Could not rotate key"); }
  };

  const poll = (runId) => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/scraper/runs/${runId}`);
        setActiveRun(data);
        if (["success", "partial", "error"].includes(data.status)) {
          clearInterval(pollRef.current);
          loadProviders();
          loadRuns();
          if (data.status === "success") toast.success(`Scrape complete · ${data.results_count} products from ${data.retailer_name}`);
          else if (data.status === "partial") toast.warning(`Scrape finished with some errors · ${data.results_count} products`);
          else toast.error(`Scrape failed — ${data.retailer_name}`);
        }
      } catch { clearInterval(pollRef.current); }
    }, 2500);
  };

  const start = async () => {
    const chosen = providers?.find((p) => p.key === retailer);
    const custom = customText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!useReceipts && custom.length === 0) { toast.error("Add at least one search term, or use community receipts."); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/admin/scraper/run", {
        retailer,
        use_receipts: useReceipts,
        queries: custom.length ? custom : undefined,
        limit: 20,
      });
      setActiveRun(data);
      if (data.status === "running") poll(data.id);
      else { loadProviders(); loadRuns(); if (data.status === "error") toast.error(chosen?.name + " couldn't run — see details below."); }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start scrape");
    } finally { setStarting(false); }
  };

  const runNow = async () => {
    setStarting(true);
    try {
      await api.post("/admin/scraper/auto-run-now");
      toast.success("Monthly scrape started (community list + 500-item basket on Takealot) · summary will be emailed");
      setTimeout(() => { loadProviders(); loadRuns(); }, 3000);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not run monthly scrape");
    } finally { setStarting(false); }
  };

  if (providers === null) return <Loading label="Loading scraper…" />;

  const selected = providers.find((p) => p.key === retailer);
  const isLive = selected?.status === "live";
  const pct = activeRun?.queries_count ? Math.round((activeRun.progress || 0) / activeRun.queries_count * 100) : 0;

  return (
    <div className="space-y-6" data-testid="admin-scraper">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-head text-xl font-bold text-riq-primary flex items-center gap-2"><Storefront size={22} weight="duotone" /> Market price scraper</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Collect retailer prices for the 500-item Household Price Index basket and <b>Where's it cheapest?</b>. Live retailers run on our server; the rest run from a worker script you generate below.</p>
        </div>
        <button data-testid="scraper-refresh" onClick={() => { loadProviders(); loadRuns(); }} className="shrink-0 rounded-full border border-riq-border bg-white px-3 py-2 text-sm font-medium hover:border-riq-primary flex items-center gap-1.5"><ArrowClockwise size={15} /> Refresh</button>
      </div>

      <ScraperRetailers ingest={ingest} onRunsChanged={() => { loadProviders(); loadRuns(); }} />

      <div className="pt-2 border-t border-riq-border">
        <div className="font-head text-base font-bold">Custom scrape</div>
        <p className="text-xs text-muted-foreground">Run your own search terms or the community list against one retailer.</p>
      </div>

      {/* Provider picker */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Retailer</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="scraper-providers">
          {providers.map((p) => (
            <button key={p.key} data-testid={`provider-${p.key}`} onClick={() => setRetailer(p.key)}
              className={`text-left rounded-2xl border p-3 transition-all ${retailer === p.key ? "border-riq-primary bg-riq-primary/5" : "border-riq-border bg-white hover:border-riq-primary/40"}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm text-slate-800">{p.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${p.status === "live" ? "bg-[#E4F7ED] text-[#1f7a48]" : "bg-neutral-100 text-neutral-400"}`}>{p.status}</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Database size={11} /> {p.products} prices</div>
            </button>
          ))}
        </div>
        {!isLive && (
          <p data-testid="provider-planned-note" className="text-xs text-riq-accent mt-2 flex items-center gap-1.5"><WarningCircle size={14} />
            {selected?.status === "external"
              ? `${selected?.name} is scraped by the standalone Playwright worker (scraper/external_worker/) on your own infra — its prices appear here automatically once written. Running it from here will report an error.`
              : `${selected?.name} isn't enabled yet — it needs the external scraping worker (headless browser + proxies).`}
          </p>
        )}
        {schedule && (
          <p data-testid="scraper-schedule" className="text-xs text-muted-foreground mt-2 flex items-center gap-2 flex-wrap">
            <span>Auto-runs live retailers on the <b>{schedule.day}th</b> of each month · {schedule.last_run_at ? `last auto-run ${new Date(schedule.last_run_at).toLocaleDateString()}` : "no auto-run yet"}</span>
            <button data-testid="scraper-run-now" onClick={runNow} disabled={starting} className="rounded-full border border-riq-primary text-riq-primary px-2.5 py-1 text-[11px] font-semibold hover:bg-riq-primary/5 disabled:opacity-40">Run monthly scrape now</button>
          </p>
        )}
      </div>

      {/* Search source */}
      <Card className="space-y-4">
        <div className="text-sm font-semibold text-slate-800">Search terms</div>
        <div className="flex items-center gap-3">
          <button data-testid="src-receipts" onClick={() => setUseReceipts(true)} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium flex items-center gap-2 justify-center ${useReceipts ? "border-riq-primary bg-riq-primary/5 text-riq-primary" : "border-riq-border text-neutral-600"}`}><Receipt size={16} weight="duotone" /> From community receipts</button>
          <button data-testid="src-custom" onClick={() => setUseReceipts(false)} className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium ${!useReceipts ? "border-riq-primary bg-riq-primary/5 text-riq-primary" : "border-riq-border text-neutral-600"}`}>Custom list</button>
        </div>
        {useReceipts ? (
          <div>
            <div className="text-xs text-muted-foreground mb-2">Top {queries.length} product names scanned by the community will be searched:</div>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="receipt-queries">
              {queries.map((q) => (
                <span key={q.query} className="rounded-full bg-riq-sage/60 text-riq-primary px-2.5 py-1 text-xs font-medium">{q.query} <span className="text-riq-primary/50">· {q.count}</span></span>
              ))}
              {queries.length === 0 && <span className="text-sm text-muted-foreground">No receipt items yet.</span>}
            </div>
          </div>
        ) : (
          <textarea data-testid="custom-queries" value={customText} onChange={(e) => setCustomText(e.target.value)} rows={4}
            placeholder="One item per line (or comma-separated), e.g.&#10;milk&#10;coffee&#10;washing powder"
            className="w-full rounded-xl border border-riq-border p-3 text-sm outline-none focus:border-riq-primary" />
        )}
        <button data-testid="run-scraper-btn" onClick={start} disabled={starting || activeRun?.status === "running"}
          className="rounded-full bg-riq-primary text-white px-5 py-3 text-sm font-semibold flex items-center gap-2 disabled:opacity-40 hover:-translate-y-0.5 transition-transform">
          {starting || activeRun?.status === "running" ? <CircleNotch size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
          {activeRun?.status === "running" ? "Running…" : `Run ${selected?.name} scrape`}
        </button>
      </Card>

      {/* Active run */}
      {activeRun && (
        <Card data-testid="active-run" className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-800 flex items-center gap-2">{activeRun.retailer_name} <StatusPill status={activeRun.status} /></div>
            <div className="text-xs text-muted-foreground">{activeRun.results_count} products</div>
          </div>
          {activeRun.status === "running" && (
            <div>
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-riq-primary transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
              <div className="text-[11px] text-muted-foreground mt-1">{activeRun.progress || 0} / {activeRun.queries_count} searches</div>
            </div>
          )}
          {activeRun.errors?.length > 0 && (
            <div data-testid="run-errors" className="rounded-xl bg-red-50 border border-red-100 p-3 space-y-1 max-h-44 overflow-y-auto">
              {activeRun.errors.map((e, i) => (
                <div key={i} className="text-xs text-red-600"><b>{e.query}</b>: {e.error}</div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Recent runs */}
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-2">Recent runs</div>
        {runs.length === 0 ? (
          <Card className="text-sm text-muted-foreground text-center py-6">No runs yet.</Card>
        ) : (
          <Card className="p-0 divide-y divide-riq-border" data-testid="scraper-runs">
            {runs.map((r) => (
              <div key={r.id} data-testid={`run-row-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                <StatusPill status={r.status} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800">{r.retailer_name}</div>
                  <div className="text-xs text-muted-foreground">{new Date(r.started_at).toLocaleString()} · {r.queries_count} searches{r.triggered_by ? ` · ${r.triggered_by}` : ""}</div>
                </div>
                <div className="text-sm font-semibold text-riq-primary shrink-0">{r.results_count} <span className="text-xs font-normal text-muted-foreground">products</span></div>
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* External worker ingest key */}
      {ingest && (
        <Card data-testid="ingest-key-card" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="font-semibold text-slate-800 flex items-center gap-2"><Key size={18} weight="duotone" /> External worker key</div>
            <button data-testid="rotate-key-btn" onClick={rotateKey} className="rounded-full border border-riq-primary text-riq-primary px-3 py-1.5 text-xs font-semibold hover:bg-riq-primary/5 flex items-center gap-1.5"><ArrowClockwise size={13} /> Rotate</button>
          </div>
          <p className="text-xs text-muted-foreground">Your off-site scraper worker uses this key to push prices in. Keep it secret. Rotating it invalidates the old key immediately.{ingest.source === "env" && " (Currently from the server env — rotate to store a managed key you can change here anytime.)"}</p>
          <div className="flex items-center gap-2">
            <code data-testid="ingest-key-value" className="flex-1 min-w-0 truncate rounded-lg bg-secondary px-3 py-2 text-sm font-mono">{showKey ? (ingest.token || "—") : "•".repeat((ingest.token || "").length || 24)}</code>
            <button data-testid="toggle-key-btn" onClick={() => setShowKey((v) => !v)} className="rounded-lg border border-riq-border p-2 hover:border-riq-primary" title={showKey ? "Hide" : "Reveal"}>{showKey ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
            <button data-testid="copy-key-btn" onClick={() => copy(ingest.token || "")} className="rounded-lg border border-riq-border p-2 hover:border-riq-primary" title="Copy"><Copy size={16} /></button>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">Run your worker (any machine)</div>
            <div className="flex items-start gap-2">
              <code className="flex-1 min-w-0 rounded-lg bg-slate-900 text-slate-100 px-3 py-2 text-[11px] font-mono whitespace-pre-wrap break-all">{`SCRAPER_INGEST_URL=${ingest.ingest_url} \\\nSCRAPER_INGEST_TOKEN=${showKey ? (ingest.token || "<key>") : "<key>"} \\\npython worker.py --retailer pnp`}</code>
              <button data-testid="copy-cmd-btn" onClick={() => copy(`SCRAPER_INGEST_URL=${ingest.ingest_url} SCRAPER_INGEST_TOKEN=${ingest.token || ""} python worker.py --retailer pnp`)} className="rounded-lg border border-riq-border p-2 hover:border-riq-primary shrink-0" title="Copy command"><Copy size={16} /></button>
            </div>
          </div>
          {ingest.updated_at && <div className="text-[11px] text-muted-foreground">Last rotated {new Date(ingest.updated_at).toLocaleString()}{ingest.updated_by ? ` by ${ingest.updated_by}` : ""}</div>}
        </Card>
      )}
    </div>
  );
}
